/**
 * Syntax Highlight Nano - Frontend JavaScript
 * Using highlight.js (BSD 3-Clause License)
 * Copyright (c) 2006, Ivan Sagalaev
 * https://github.com/highlightjs/highlight.js
 */
import hljs from "highlight.js/lib/core";

// WordPress i18n グローバル変数を使用
const { __ } = wp.i18n;

// 主要な言語をインポート
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import php from "highlight.js/lib/languages/php";
import python from "highlight.js/lib/languages/python";
import ruby from "highlight.js/lib/languages/ruby";
import java from "highlight.js/lib/languages/java";
import c from "highlight.js/lib/languages/c";
import cpp from "highlight.js/lib/languages/cpp";
import csharp from "highlight.js/lib/languages/csharp";
import go from "highlight.js/lib/languages/go";
import rust from "highlight.js/lib/languages/rust";
import swift from "highlight.js/lib/languages/swift";
import kotlin from "highlight.js/lib/languages/kotlin";
import xml from "highlight.js/lib/languages/xml"; // HTML, XML
import css from "highlight.js/lib/languages/css";
import scss from "highlight.js/lib/languages/scss";
import less from "highlight.js/lib/languages/less";
import json from "highlight.js/lib/languages/json";
import yaml from "highlight.js/lib/languages/yaml";
import markdown from "highlight.js/lib/languages/markdown";
import sql from "highlight.js/lib/languages/sql";
import bash from "highlight.js/lib/languages/bash";
import powershell from "highlight.js/lib/languages/powershell";
import applescript from "highlight.js/lib/languages/applescript";

// 言語を登録
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("php", php);
hljs.registerLanguage("python", python);
hljs.registerLanguage("ruby", ruby);
hljs.registerLanguage("java", java);
hljs.registerLanguage("c", c);
hljs.registerLanguage("cpp", cpp);
hljs.registerLanguage("csharp", csharp);
hljs.registerLanguage("go", go);
hljs.registerLanguage("rust", rust);
hljs.registerLanguage("swift", swift);
hljs.registerLanguage("kotlin", kotlin);
hljs.registerLanguage("xml", xml);
hljs.registerLanguage("html", xml); // HTMLはXMLと同じ
hljs.registerLanguage("css", css);
hljs.registerLanguage("scss", scss);
hljs.registerLanguage("less", less);
hljs.registerLanguage("json", json);
hljs.registerLanguage("yaml", yaml);
hljs.registerLanguage("markdown", markdown);
hljs.registerLanguage("sql", sql);
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("shell", bash); // shellはbashと同じ
hljs.registerLanguage("powershell", powershell);
hljs.registerLanguage("applescript", applescript);

// JSX/TSX用のエイリアス（JavaScriptとして処理）
hljs.registerLanguage("jsx", javascript);
hljs.registerLanguage("tsx", typescript);

/**
 * 行番号を追加
 */
function addLineNumbers(codeElement) {
  const preElement = codeElement.closest("pre.synano");
  if (!preElement || !preElement.classList.contains("has-line-numbers")) {
    return;
  }

  // 既に行番号が追加されている場合はスキップ
  if (codeElement.querySelector(".hljs-ln")) {
    return;
  }

  // ハイライト済みのHTMLを保持
  const originalHTML = codeElement.innerHTML;
  const lines = originalHTML.split("\n");

  // テーブル形式で再構築
  let tableHTML = '<table class="hljs-ln"><tbody>';
  lines.forEach((lineHTML, index) => {
    const lineNum = index + 1;
    tableHTML += `<tr>
      <td class="hljs-ln-numbers"><div class="hljs-ln-line" data-line-number="${lineNum}">${lineNum}</div></td>
      <td class="hljs-ln-code"><div class="hljs-ln-line">${
        lineHTML || "&nbsp;"
      }</div></td>
    </tr>`;
  });
  tableHTML += "</tbody></table>";

  codeElement.innerHTML = tableHTML;
}

/**
 * コピーボタンを追加
 */
function addCopyButton(preElement) {
  // 既にボタンが追加されている場合はスキップ
  if (preElement.querySelector(".synano-copy-button")) {
    return;
  }

  // コピーアイコンSVG
  const copyIcon = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block; width: 14px; height: 14px;">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  `;

  // チェックマークアイコンSVG
  const checkIcon = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 6L9 17l-5-5"></path>
    </svg>
  `;

  // コピーボタンを作成
  const button = document.createElement("button");
  button.className = "synano-copy-button";
  button.innerHTML = `${copyIcon}<span>${__(
    "Copy",
    "syntax-highlight-nano"
  )}</span>`;
  button.setAttribute(
    "aria-label",
    __("Copy code to clipboard", "syntax-highlight-nano")
  );

  // クリックイベント
  button.addEventListener("click", async () => {
    const codeElement = preElement.querySelector("code");
    if (!codeElement) return;

    try {
      // コードのテキストを取得（行番号を除く）
      let code;
      if (preElement.classList.contains("has-line-numbers")) {
        // 行番号がある場合、.hljs-ln-codeからテキストを取得
        const codeLines = preElement.querySelectorAll(".hljs-ln-code");
        code = Array.from(codeLines)
          .map((line) => line.textContent)
          .join("\n");
      } else {
        code = codeElement.textContent;
      }

      // クリップボードにコピー
      await navigator.clipboard.writeText(code);

      // 成功フィードバック
      button.innerHTML = `${checkIcon}<span>${__(
        "Copied!",
        "syntax-highlight-nano"
      )}</span>`;
      button.classList.add("copied");

      // 2秒後に元に戻す
      setTimeout(() => {
        button.innerHTML = `${copyIcon}<span>${__(
          "Copy",
          "syntax-highlight-nano"
        )}</span>`;
        button.classList.remove("copied");
      }, 2000);
    } catch {
      // コピーに失敗した場合の処理
      button.innerHTML = `${copyIcon}<span>${__(
        "Failed",
        "syntax-highlight-nano"
      )}</span>`;
      setTimeout(() => {
        button.innerHTML = `${copyIcon}<span>${__(
          "Copy",
          "syntax-highlight-nano"
        )}</span>`;
      }, 2000);
    }
  });

  preElement.appendChild(button);
}

/**
 * RGB値を明度に変換
 * @param {number} r 赤
 * @param {number} g 緑
 * @param {number} b 青
 * @returns {number} 明度 (0-255)
 */
function getLuminance(r, g, b) {
  // 相対輝度の計算 (WCAG 2.1)
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * RGB文字列をパース
 * @param {string} rgbString "rgb(255, 255, 255)" 形式の文字列
 * @returns {Array<number>} [r, g, b] の配列
 */
function parseRgb(rgbString) {
  const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return [0, 0, 0];
  return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
}

/**
 * 背景色に応じてテキスト色を決定
 * @param {string} backgroundColor RGB文字列
 * @returns {string} テキスト色
 */
function getContrastTextColor(backgroundColor) {
  const [r, g, b] = parseRgb(backgroundColor);
  const luminance = getLuminance(r, g, b);

  // 明度が0.5より高い場合は暗いテキスト、低い場合は明るいテキスト
  return luminance > 0.5 ? "#333333" : "#dddddd";
}

/**
 * 行番号の背景色をhljsの背景色に合わせる
 * @param {Element} preElement pre要素
 * @param {string} customBackgroundColor カスタム背景色（オプション）
 */
function updateLineNumbersBackground(preElement, customBackgroundColor = null) {
  const codeElement = preElement.querySelector("code");
  if (!codeElement) return;

  // カスタム背景色が指定されている場合はそれを使用
  let backgroundColor = customBackgroundColor;

  if (!backgroundColor) {
    // hljsの背景色を取得
    const computedStyle = window.getComputedStyle(codeElement);
    backgroundColor = computedStyle.backgroundColor;
  }

  // 行番号の要素に背景色を適用
  const lineNumbers = preElement.querySelectorAll(".hljs-ln-numbers");
  lineNumbers.forEach((lineNumber) => {
    lineNumber.style.backgroundColor = backgroundColor;
  });

  // コピーボタンの色も更新
  updateCopyButtonColors(preElement, backgroundColor);
}

/**
 * コピーボタンの色を背景色に応じて調整
 * @param {Element} preElement pre要素
 * @param {string} backgroundColor 背景色
 */
function updateCopyButtonColors(preElement, backgroundColor) {
  const copyButton = preElement.querySelector(".synano-copy-button");
  if (!copyButton) return;

  const textColor = getContrastTextColor(backgroundColor);

  // コピーボタンの色を設定
  copyButton.style.color = textColor;
  copyButton.style.borderColor = textColor + "20"; // 40%透明度
}

/**
 * 言語コードから表示用のラベルを取得
 * @param {string} language 言語コード
 * @returns {string} 表示用の言語ラベル
 */
function getLanguageLabel(language) {
  // PHPから渡された言語ラベル配列を使用
  const labels = window.synanoLanguageLabels || {};

  // マッピングにあればそれを返す、なければ最初の文字を大文字に変換
  return (
    labels[language] || language.charAt(0).toUpperCase() + language.slice(1)
  );
}

/**
 * コードブロックをハイライト
 */
function highlightCode() {
  // すべてのコードブロックを取得
  const codeBlocks = document.querySelectorAll(
    "pre.synano code[class*='language-']"
  );

  codeBlocks.forEach((block) => {
    // 既にハイライト済みの場合はスキップ
    if (block.classList.contains("hljs")) {
      return;
    }

    // 言語が'plaintext'の場合はhljsクラスのみ追加（ハイライトなし）
    if (block.classList.contains("language-plaintext")) {
      // hljsクラスを追加してテーマのスタイルを適用
      block.classList.add("hljs");
      // ハイライトは適用しないが、テーマのスタイルは適用される
    }
    // language-autoの場合は自動検出を有効にする
    else if (block.classList.contains("language-auto")) {
      const result = hljs.highlightAuto(block.textContent);
      block.innerHTML = result.value;
      block.classList.add("hljs", `language-${result.language}`);

      // ヘッダーの言語表示を更新
      const preElement = block.closest("pre.synano");
      if (preElement && preElement.classList.contains("has-header")) {
        const currentHeader = preElement.getAttribute("data-header");
        // ヘッダーが空の場合のみ、検出された言語名に更新
        if (currentHeader === "") {
          const languageLabel = getLanguageLabel(result.language);
          preElement.setAttribute("data-header", languageLabel);
        }
      }
    } else {
      // 指定された言語でハイライトを適用
      hljs.highlightElement(block);
    }

    // 行番号を追加
    addLineNumbers(block);

    // コピーボタンを追加
    const preElement = block.closest("pre.synano");
    if (preElement) {
      addCopyButton(preElement);
      // 行番号の背景色を更新
      updateLineNumbersBackground(preElement);
    }
  });
}

/**
 * 初期化とイベント設定
 */
function init() {
  // 初回実行
  highlightCode();

  // 動的コンテンツ対応
  if (window.MutationObserver) {
    const observer = new MutationObserver(() => {
      highlightCode();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

// DOMContentLoaded時に実行
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// グローバルに公開（デバッグ用）
window.synano = {
  hljs,
  refresh: highlightCode,
};
